import React, { createContext, useReducer } from 'react';

import QuestionAnswerIcon from '@material-ui/icons/QuestionAnswer';
import PhotoLibraryIcon from '@material-ui/icons/PhotoLibrary';
import HomeIcon from '@material-ui/icons/Home';
import WhatshotIcon from '@material-ui/icons/Whatshot';
import KitchenIcon from '@material-ui/icons/Kitchen';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import AssignmentIcon from '@material-ui/icons/Assignment';
import { ausumOrLc360FormHeaderIds, checkIfAusumOrLc360 } from '../../../enums/formHeaders';
import { coverageCategories } from '../../../enums';
import { checkIfRCT } from '../../../enums/formHeaders';

// export const ADD = 'DROP_IMAGE';
export const UPDATE_IMAGES = 'UPDATE_IMAGES';
export const UPDATE_ORDER = 'UPDATE_ORDER';
export const UPDATE_ORDER_INFO = 'UPDATE_ORDER_INFO';
export const UPDATE_ORDER_DATA = 'UPDATE_ORDER_DATA';
export const UPDATE_ORDER_FORMS = 'UPDATE_ORDER_FORMS';
export const UPDATE_INSPECTION_STEPS = 'UPDATE_INSPECTION_STEPS';

const searchQuestionForImagesPartialObj = ({
  answers,
  questionId,
  answerTriggers,
  question: { value: questionLabel },
}) => {
  const images = (answers
    ? answers
        .map(ans =>
          ans.formFieldRepAnswerImage
            ? ans.formFieldRepAnswerImage.map(({ imageUri, formFieldRepImageId }) => ({
                src: imageUri,
                formFieldRepImageId,
                questionId,
                questionLabel,
              }))
            : null,
        )
        .filter(ans => ans) // filtering out the nulls
    : []
  ).flat();

  if (answerTriggers && answerTriggers.length) {
    const nestedImages = answerTriggers
      .map(at =>
        at.triggeredSub && at.triggeredSub.length
          ? at.triggeredSub.map(ts => searchQuestionForImagesPartialObj(ts))
          : [],
      )
      .flat();
    return images.concat(nestedImages).flat();
  }
  return images.flat();
};

const searchQuestionForImagesFullObj = ({ answers, answerTriggers }) => {
  const images = answers ? answers.map(ans => ans.formFieldRepAnswerImage) : [];

  if (answerTriggers && answerTriggers.length) {
    const nestedImages = answerTriggers
      .map(at =>
        at.triggeredSub && at.triggeredSub.length ? at.triggeredSub.map(ts => searchQuestionForImagesFullObj(ts)) : [],
      )
      .flat();
    return images.concat(nestedImages).flat();
  }
  return images.flat();
};

const createImageDictionary = (forms, newMiscImages = []) => {
  const formImages = forms
    .map(fh => {
      const miscImages = fh.miscImages || [];

      // const qImages = fh.formQuestions.map(({ answers }) => answers.map(ans => ans.formFieldRepAnswerImage));

      const qImages = fh.formQuestions.map(searchQuestionForImagesFullObj);

      return [...miscImages, ...qImages].flat();
    })
    .flat()
    .flat(); // bringing all image objects up to the top

  const allImages = [...newMiscImages, ...formImages].reduce((acc, cur) => {
    // check if the image already exists
    if (acc[cur.formFieldRepImageId]) {
      return acc;
    }

    acc[cur.formFieldRepImageId] = cur;

    return acc;
  }, {});

  return allImages;
};

const addRelatedQuestionsToImages = (forms, newMiscImages = []) => {
  /**
   * gets all images down to src, questionLabel, & questionId
   */
  const formImages = forms
    .map(fh => {
      const misc = (fh.miscImages
        ? fh.miscImages.map(({ imageUri, formFieldRepImageId }) => ({
            src: imageUri,
            formFieldRepImageId,
            questionId: null,
            questionLabel: null,
          }))
        : []
      ).flat();

      const qImages = fh.formQuestions
        .map(searchQuestionForImagesPartialObj)
        // .map(({ answers, questionId, question: { value: questionLabel } }) =>
        //   (answers
        //     ? answers
        //         .map(ans =>
        //           ans.formFieldRepAnswerImage
        //             ? ans.formFieldRepAnswerImage.map(({ imageUri, formFieldRepImageId }) => ({
        //                 src: imageUri,
        //                 formFieldRepImageId,
        //                 questionId,
        //                 questionLabel,
        //               }))
        //             : null,
        //         )
        //         .filter(ans => ans) // filtering out the nulls
        //     : []
        //   ).flat(),
        // )
        .flat();

      return [...misc, ...qImages];
    })
    .flat();

  const miscImages = newMiscImages.map(i => ({ ...i, src: i.imageUri, questionId: null, questionLabel: null }));

  const mapped = [...miscImages, ...formImages];

  /**
   * reduces all related images by questions
   * . if an image belongs to two questions, it will have one object with an array of questions
   */
  const reduced = mapped.reduce((acc, { src, questionId, questionLabel, formFieldRepImageId }) => {
    // find if this image has already been used
    const imageAccIndex = acc.findIndex(i => i.src === src);

    const questionInfo = {
      questionId,
      questionLabel,
    };

    if (imageAccIndex !== -1) {
      // add it to the existing index if questionId exists (otherwise is it a miscellaneous photo)
      !!questionId && acc[imageAccIndex].questions.push(questionInfo);
    } else {
      // create a new index
      acc.push({
        src,
        formFieldRepImageId,
        questions: !!questionId ? [questionInfo] : [], // check if the photo is misc. or not
      });
    }

    return acc;
  }, []);

  return reduced;
};

const removeDuplicates = (myArr, prop) => {
  return myArr.filter((obj, pos, arr) => {
    return arr.map(mapObj => mapObj[prop]).indexOf(obj[prop]) === pos;
  });
};

/**
 * @function groupByArray
 * @desc group a 1-D array of items into a 2-D array (with related objects in each nested array)
 * @param {*} origArr
 * @param {*} key
 * @param {*} key2 (optional) defines a second property to check against
 */
const groupByArray = (origArr, key, key2 = null) => {
  return origArr.reduce((acc, cur) => {
    let keyResult1 = key instanceof Function ? key(cur) : cur[key];
    let keyResult2 = !key2 ? null : key2 instanceof Function ? key2(cur) : cur[key2];

    let el = acc.find(r => r && r.key === keyResult1 && (!r.key2 || r.key2 === keyResult2));
    if (el) {
      el.values.push(cur);
    } else {
      acc.push({ key: keyResult1, key2: keyResult2, values: [cur] });
    }
    return acc;
  }, []);
};

const findStandaloneQuestions = ({ orderQuestions = [], orderForms = [], order = {} }) => {
  // if (
  //   orderQuestions.some(
  //     q =>
  //       !q.covered &&
  //       // TODO: revisit if we want to limit by questioncategory or not q.questionCategory == "Risk Characteristic" &&
  //       q.formContext == 'Standalone Formheader',
  //   )
  // ) {
  // let standAloneQuestions = orderQuestions.filter(q => !q.covered && q.formContext == 'Standalone Formheader');

  let standAloneGroups = groupByArray(orderQuestions, 'formHeaderId', 'orderFormHeaderId');
  let standAloneFormHeaders = [];

  for (let sag of standAloneGroups) {
    // find all related form headers (same formHeader but different orderFormHeaderId)
    const relatedFormHeaders = orderForms.filter(of => of.formHeaderId === sag.key);

    const formHeaderIndex = relatedFormHeaders.findIndex(
      of => of.formHeaderId === sag.key && (!sag.key2 || sag.key2 === of.orderFormHeaderId),
    );

    const formHeader = relatedFormHeaders[formHeaderIndex];
    const formHeaderName =
      formHeader?.formHeaderName + (relatedFormHeaders.length > 1 ? ` #${formHeaderIndex + 1}` : '') ??
      'Additional Questions';
    const formHeaderId = formHeader?.formHeaderId ?? null;
    const orderFormHeaderId = formHeader?.orderFormHeaderId ?? null;
    const isRemovable = formHeader?.isRemovable ?? false;

    //sag.values
    standAloneFormHeaders.push({
      label: formHeaderName,
      formHeaderId,
      orderFormHeaderId,
      name: formHeaderName,
      isRemovable,
      menu: true,
      icon: <AssignmentIcon />,
      orientation: 'portrait',
      hideHeader: false,
      type: 'inspectionForm',
      stepData: {
        formHeaderId,
        formContext: formHeaderName,
        //
        questions: sag.values,
        orderForms: orderForms,
        // goToNextStep: this.goToNextStep,
        orderLineId: order.orderLineId,
        // setOrderFormConstructionAdvisory: this.setOrderFormConstructionAdvisory,
        // addFormHeader: this.addFormHeader,
      },
    });
  }

  return standAloneFormHeaders;
  // } else {
  //   return [];
  // }
};

/**
 * @func upsertInspectionSteps
 * @desc adds/removes a formHeader from the order
 * @param {array } existingSteps non-mutating array
 * @param { object } formHeader    the new form header to add/update/remove
 * @param { object } opts {
 *    remove {bool} - if the formHeader exists, it will remove it (otherwise it will update it)
 * }
 */
// const upsertInspectionSteps = (
//   existingSteps,
//   formHeader,
//   opts = {
//     remove: false,
//   },
// ) => {
//   const index = existingSteps.findIndex(s => s.name === formHeader.name);

//   if (index === -1) {

//     // justin's logic
//     // if (this.currentStep != 0) {
//     //   //TODO if orderFormHeaderId, add to after building info?
//     //   //FR desktop will not be able to key on currentstep != 0
//     //   this.inspectionSteps.splice(this.currentStep + 1, 0, formHeader);
//     // } else {
//     //   this.inspectionSteps.push(formHeader);
//     // }
//   } else {
//     //logic to avoid changing the current step?
//     this.inspectionSteps[index] = formHeader;
//   }
// };

/**
 * @func findOrderFormIndex
 * @desc finds an order form index to do one of the following:
 *  - delete based on orderFormHeaderId and coverage category
 *  -
 * @param {*} orderForms
 * @param {*} form
 */
const findOrderFormIndex = (orderForms, form) => {
  /**
   * I'm being verbose with the if logic here to make it easier to understand later
   */

  // check if coverage category is Supplemental Single (in which case there can not be more than one instance of that formHeader)
  const isSupplementalSingle = form.coverageCategoryId === coverageCategories.SUPPLEMENTAL_SINGLE;

  const formIndex = orderForms.findIndex(of => {
    // checks if this order is a match
    const isOrderMatch = of.formHeaderId === form.formHeaderId;

    // checks if this instance of formHeader is a match
    const isOrderFormHeaderMatch = of.orderFormHeaderId === form.orderFormHeaderId;

    // if it's not a match, move on
    if (!isOrderMatch) return false;

    // if it is a match and the new form can only have one instance, then this index is that single instance (in which it will either be removed or replaced with a new instance)
    // single instances do not care about OrderFormHeaderId since those are only necessary to distinguish multiple instances of the same formHeader
    if (isSupplementalSingle) return true;

    // if it is a match and it can have multiple instances, then use the OrderFormHeaderId to distinguish which instance to replace/remove
    if (isOrderFormHeaderMatch) return true;

    // else, this scenario did not match any existing orderFormHeaders, so the new form will be appended to the end of the existing orderForms
    return false;
  });

  return formIndex;
};

/**
 * @func upsertOrderForms
 * @desc add/update/remove an order to/from the existing order forms
 * @param { array<object> } orderForms
 * @param { object } newOrderForm
 * @param { object } opts
 */
export const upsertOrderForms = (orderForms, newOrderForm) => {
  const index = findOrderFormIndex(orderForms, newOrderForm);

  if (index === -1) {
    // add the new order form to the existing
    return orderForms.concat(newOrderForm);
  } else {
    // update the existing order form (depending on the option passed in)
    orderForms[index] = newOrderForm;

    // if (remove) {
    //   orderForms = orderForms.slice(0, index).concat(orderForms.slice(index + 1)); // non-mutating removal (unlike splice)
    // } else {
    //   orderForms[index] = newOrderForm;
    // }

    return orderForms;
  }
};

export const removeOrderForm = (orderForms, orderFormToRemove) => {
  const index = findOrderFormIndex(orderForms, orderFormToRemove);

  orderForms = orderForms.slice(0, index).concat(orderForms.slice(index + 1)); // non-mutating removal (unlike splice)

  return orderForms;
};

const configureSteps = (order = {}, orderForms = [], isSelfInspect = false) => {
  // make sure order and order forms are available
  if (!Object.keys(order).length || !orderForms.length) {
    return [];
  }

  const isRCT = checkIfRCT(orderForms);
  const isAusumOrLc360 = checkIfAusumOrLc360(orderForms);

  let steps = [];
  let allOrderQuestions = [].concat.apply(
    [],
    //orderForms.map(fo => fo.formQuestions.filter(fq => fq.formContext !== 'Standalone Formheader')),
    orderForms.filter(of => of.orderable == false).map(fo => fo.formQuestions),
  );

  let allStandaloneFormHeaderQuestions = [].concat.apply(
    [],
    //orderForms.map(fo => fo.formQuestions.filter(fq => fq.formContext === 'Standalone Formheader')),
    orderForms.filter(of => of.orderable == true || of.formHeaderId == 135).map(fo => fo.formQuestions),
  );

  //allOrderQuestions = removeDuplicates(allOrderQuestions, 'questionId');
  allOrderQuestions.forEach(q => (q.covered = false));
  allStandaloneFormHeaderQuestions.forEach(q => (q.covered = false));

  const reviewStep = {
    label: 'Review',
    menu: true,
    name: 'review',
    icon: <CheckCircleIcon />,
    orientation: 'portrait',
    hideHeader: false,
    type: 'review',
    stepData: {
      orderLineId: order.orderLineId,
    },
    isRCT,
  };

  if (isSelfInspect) {
    steps.push({
      label: 'Intro',
      menu: true,
      name: 'selfInspectIntro',
      type: 'selfInspectIntro',
      icon: <CheckCircleIcon />,
      orientation: 'portrait',
      hideHeader: false,
      stepData: {
        orderLineId: order.orderLineId,
      },
      isRCT,
    });
  }

  if (isRCT) {
    // add initial step
    steps.push({
      label: 'Inspection',
      menu: true,
      icon: <QuestionAnswerIcon />,
      orientation: 'portrait',
      hideHeader: false,
      type: 'inspectionForm',
      stepData: {
        rctquestions: orderForms[0].content,
        questions: allOrderQuestions,
        orderForms: orderForms,
        orderLineId: order.orderLineId,
      },
      isRCT,
    });

    // add review step
    steps.push(reviewStep);
    return steps;
  }

  if (isAusumOrLc360) {
    // add initial step
    steps.push({
      label: 'Inspection',
      menu: true,
      icon: <QuestionAnswerIcon />,
      orientation: 'portrait',
      hideHeader: false,
      type: 'inspectionForm',
      stepData: {
        questions: allOrderQuestions.filter(q => {
          if (!q.covered && q.formContext === 'Pre-Order' && q.questionCategory === 'Misc') {
            // if (!q.covered && ['Interview', 'Pre-Order'].includes(q.formContext)) {
            q.covered = true;
            return true;
          }
        }),
        orderForms: orderForms,
        orderLineId: order.orderLineId,
      },
      isAusumOrLc360,
    });

    // add review step
    steps.push(reviewStep);
    return steps;
  }

  //Interview
  if (allOrderQuestions.some(q => q.formContext == 'Interview')) {
    steps.push({
      label: 'Interview',
      menu: true,
      icon: <QuestionAnswerIcon />,
      orientation: 'portrait',
      hideHeader: false,
      type: 'inspectionForm',
      stepData: {
        questions: allOrderQuestions.filter(q => {
          if (!q.covered && q.formContext == 'Interview') {
            q.covered = true;
            return true;
          }
        }),
        orderForms: orderForms,
        orderLineId: order.orderLineId,
      },
    });
  }

  //Hover Inspetion
  if (allOrderQuestions.some(q => !q.covered && q.formContext == 'Hover Inspection')) {
    steps.push({
      label: 'Hover Inpsection',
      menu: true,
      icon: <PhotoLibraryIcon />,
      orientation: 'portrait',
      hideHeader: false,
      type: 'hoverInspection',
      stepData: {
        questions: allOrderQuestions.filter(q => {
          if (!q.covered && q.formContext == 'Hover Inspection') {
            q.covered = true;
            return true;
          }
        }),
        // offline: offline,
        orderForms: orderForms,
        orderLineId: order.orderLineId,
      },
    });
  }

  //Risk Characteristic
  if (
    allOrderQuestions.some(
      q =>
        !q.covered &&
        q.questionCategory == 'Risk Characteristic' &&
        q.formContext != 'Interview' &&
        q.formContext != 'Integration' &&
        q.formContext != 'Standalone Formheader',
    )
  ) {
    steps.push({
      label: 'Building Information',
      menu: true,
      icon: <HomeIcon />,
      orientation: 'portrait',
      hideHeader: false,
      type: 'inspectionForm',
      stepData: {
        questions: allOrderQuestions.filter(q => {
          if (
            !q.covered &&
            q.questionCategory == 'Risk Characteristic' &&
            q.formContext != 'Interview' &&
            q.formContext != 'Integration' &&
            q.formContext != 'Standalone Formheader' &&
            q.formContext != 'Interior Inspection'
          ) {
            q.covered = true;
            return true;
          }
        }),
        orderForms: orderForms,
        orderLineId: order.orderLineId,
      },
    });
  }

  if (
    allOrderQuestions.some(
      q =>
        !q.covered &&
        q.questionCategory == 'Special Hazard' &&
        q.formContext != 'Integration' &&
        q.formContext != 'Standalone Formheader' &&
        q.formDataSources.some(x => x == 'HazardHub'),
    )
  ) {
    steps.push({
      label: 'Fire Protection',
      menu: true,
      icon: <WhatshotIcon />,
      orientation: 'portrait',
      hideHeader: false,
      type: 'inspectionForm',
      stepData: {
        questions: allOrderQuestions.filter(q => {
          if (
            !q.covered &&
            q.questionCategory == 'Special Hazard' &&
            q.formContext != 'Standalone Formheader' &&
            q.formDataSources.some(x => x == 'HazardHub')
          ) {
            q.covered = true;
            return true;
          }
        }),
        // offline: offline,
        orderForms: orderForms,
        orderLineId: order.orderLineId,
      },
    });
  }

  // Standalone formheaders
  const safh = findStandaloneQuestions({
    // allOrderQuestions,
    orderQuestions: allStandaloneFormHeaderQuestions,
    orderForms,
    order,
  });
  steps.push(...safh);

  // Exterior Photos
  if (allOrderQuestions.some(q => q.questionCategory == 'Photo' && q.formContext == 'Property Walk-Around')) {
    steps.push({
      label: 'Exterior Photos',
      menu: true,
      icon: <PhotoLibraryIcon />,
      orientation: 'portrait',
      hideHeader: false,
      type: 'walkaroundExteriorScreen',
      stepData: {
        questions: allOrderQuestions.filter(
          q => q.questionCategory == 'Photo' && q.formContext == 'Property Walk-Around',
        ),
        formContext: 'Exterior',
        orderForms: orderForms,
        orderLineId: order.orderLineId,
      },
    });
  }

  // Interior Walk-Around
  if (allOrderQuestions.some(q => q.questionCategory == 'Photo' && q.formContext == 'Interior Walk-Around')) {
    steps.push({
      label: 'Interior Photos',
      menu: true,
      icon: <PhotoLibraryIcon />,
      orientation: 'portrait',
      hideHeader: false,
      type: 'walkaroundInteriorScreen',
      //formFieldRepAnswerImageId > 0 && answerValueId != null  then it is a
      stepData: {
        questions: allOrderQuestions.filter(
          q => q.questionCategory == 'Photo' && q.formContext == 'Interior Walk-Around',
        ),
        formContext: 'Interior',
        orderForms: orderForms,
        // offline: offline,
        orderLineId: order.orderLineId,
      },
    });
  }

  //Risk Characteristic - Interior
  if (
    allOrderQuestions.some(
      q => !q.covered && q.questionCategory == 'Risk Characteristic' && q.formContext == 'Interior Inspection',
    )
  ) {
    steps.push({
      label: 'Interior Information',
      menu: true,
      icon: <KitchenIcon />,
      orientation: 'portrait',
      hideHeader: false,
      type: 'inspectionForm',
      stepData: {
        questions: allOrderQuestions.filter(q => {
          if (
            !q.covered &&
            q.questionCategory == 'Risk Characteristic' &&
            q.formContext != 'Interview' &&
            q.formContext != 'Integration'
          ) {
            q.covered = true;
            return true;
          }
        }),
        orderForms: orderForms,
        orderLineId: order.orderLineId,
      },
    });
  }

  // exterior conditions & liabilities
  if (
    allOrderQuestions.some(
      q =>
        (q.questionCategory == 'Liability' || q.questionCategory == 'Condition') && !q.formContext.includes('Interior'),
    )
  ) {
    steps.push({
      label: 'Exterior Conditions & Liabilities',
      menu: true,
      icon: <KitchenIcon />,
      orientation: 'portrait',
      hideHeader: false,
      type: 'inspectionForm',
      stepData: {
        questions: allOrderQuestions
          .filter(
            q =>
              (q.questionCategory == 'Liability' || q.questionCategory == 'Condition') &&
              !q.formContext.includes('Interior'),
          )
          .sort((a, b) => (a.question.value > b.question.value ? 1 : -1)),
        orderForms: orderForms,
        orderLineId: order.orderLineId,
      },
    });
  }

  // interior conditions & liabilities
  if (
    allOrderQuestions.some(
      q =>
        (q.questionCategory == 'Liability' || q.questionCategory == 'Condition') && q.formContext.includes('Interior'),
    )
  ) {
    steps.push({
      label: 'Interior Conditions & Liabilities',
      menu: true,
      icon: <KitchenIcon />,
      orientation: 'portrait',
      hideHeader: false,
      type: 'inspectionForm',
      stepData: {
        questions: allOrderQuestions
          .filter(
            q =>
              (q.questionCategory == 'Liability' || q.questionCategory == 'Condition') &&
              q.formContext.includes('Interior'),
          )
          .sort((a, b) => (a.question.value > b.question.value ? 1 : -1)),
        orderForms: orderForms,
        orderLineId: order.orderLineId,
      },
    });
  }

  // push the review step so that it is last
  steps.push(reviewStep);

  return steps;
};

let reducer = (state, action) => {
  switch (action.type) {
    case UPDATE_IMAGES:
      const newDict = createImageDictionary(state.orderForms, action.images);
      const newDictWithRelatedQuestions = addRelatedQuestionsToImages(state.orderForms, action.images);

      return {
        ...state,
        imageDictionary: newDict,
        imagesWithQuestions: newDictWithRelatedQuestions,
      };

    case UPDATE_ORDER:
      return {
        ...state,
        order: action.order,
        isSelfInspect: !!action.order.orderLineGuid,
        inspectionSteps: configureSteps(action.order, state.orderForms, state.isSelfInspect),
      };

    case UPDATE_ORDER_INFO:
      return {
        ...state,
        orderInfo: action.orderInfo,
      };

    case UPDATE_ORDER_DATA:
      return {
        ...state,
        orderData: action.orderData,
      };

    case UPDATE_ORDER_FORMS:
      const dict = createImageDictionary(action.orderForms);
      const dictWithRelatedQuestions = addRelatedQuestionsToImages(action.orderForms);

      return {
        ...state,
        orderForms: action.orderForms,
        imageDictionary: dict,
        imagesWithQuestions: dictWithRelatedQuestions,
        inspectionSteps: configureSteps(state.order, action.orderForms, state.isSelfInspect),
      };

    case UPDATE_INSPECTION_STEPS:
      return {
        ...state,
        inspectionSteps: action.inspectionSteps,
      };

    // case DROP_IMAGE:
    //   return {
    //     ...state,
    //     // ...handleDropImage(state, action),
    //   };

    default:
      return state;
  }
};

const initialState = {
  order: null, // coming from inspector API
  orderInfo: [],
  orderData: {}, // coming from order API (used by QA order review)
  orderForms: [],
  imageDictionary: [],
  imagesWithQuestions: [],
  inspectionSteps: [],

  isSelfInspect: false,
};

export const InspectorOrderReviewContext = createContext(initialState);

export const InspectorOrderReviewContextProvider = ({ children, ...rest }) => {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    ...rest,
  });

  return (
    <InspectorOrderReviewContext.Provider value={{ state, dispatch }}>{children}</InspectorOrderReviewContext.Provider>
  );
};
